import { useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ASSETS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AlertsBell from "@/components/AlertsBell";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { LayoutDashboard, Users, FileSearch, FileSpreadsheet, LogOut, PlusCircle, Home as HomeIcon, ShieldAlert, Settings, Building2, Newspaper, Database, FileCheck, ShieldCheck, CalendarClock, Archive, MapPin, FilePlus2, GitCompare, Mail } from "lucide-react";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const isAdmin = user?.role === "super_admin";
  const userPerms = user?.permissoes || [];
  const hasPerm = (p) => isAdmin || userPerms.includes(p);

  const items = useMemo(() => [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, show: hasPerm("dashboard") },
    { to: "/admin/inscricoes", label: "Inscrições", icon: FileSearch, show: hasPerm("inscricoes.view") },
    { to: "/admin/vulnerabilidades", label: "Vulnerabilidades", icon: ShieldAlert, show: hasPerm("vulnerabilidades") },
    { to: "/admin/mapa-bairros", label: "Mapa por Bairro", icon: MapPin, show: hasPerm("dashboard") },
    { to: "/admin/dossie", label: "Dossiê", icon: FilePlus2, show: hasPerm("inscricoes.view") },
    { to: "/admin/dossie/comparar", label: "Comparar Dossiês", icon: GitCompare, show: hasPerm("inscricoes.view") },
    { to: "/admin/nova-inscricao", label: "Nova Inscrição", icon: PlusCircle, show: hasPerm("nova-inscricao") },
    { to: "/admin/usuarios", label: "Usuários", icon: Users, show: isAdmin },
    { to: "/admin/empreendimentos", label: "Configuração da Página", icon: Building2, show: isAdmin },
    { to: "/admin/informacoes", label: "Informações", icon: Newspaper, show: isAdmin },
    { to: "/admin/documentos-obrigatorios", label: "Documentação Obrigatória", icon: FileCheck, show: isAdmin },
    { to: "/admin/periodo-inscricoes", label: "Período de Inscrições", icon: CalendarClock, show: isAdmin },
    { to: "/admin/config-atualizacao", label: "Atualização Cadastral", icon: Settings, show: isAdmin },
    { to: "/admin/alertas-email", label: "Alertas por E-mail", icon: Mail, show: isAdmin },
    { to: "/admin/pontos-apoio", label: "Pontos de Apoio", icon: MapPin, show: isAdmin },
    { to: "/admin/banco-de-dados", label: "Banco de Dados", icon: Database, show: isAdmin },
    { to: "/admin/backup-browser", label: "Arquivo de Backups", icon: Archive, show: isAdmin },
    { to: "/admin/auditoria", label: "Auditoria", icon: ShieldCheck, show: isAdmin },
    { to: "/admin/relatorios", label: "Relatórios", icon: FileSpreadsheet, show: hasPerm("inscricoes.exportar") },
  ].filter((i) => i.show), [isAdmin, userPerms]);  // eslint-disable-line react-hooks/exhaustive-deps

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen flex bg-[#F4F6F9]">
      <aside className="hidden md:flex w-64 bg-[#083CA6] text-white flex-col">
        <div className="govbr-stripe" />
        <div className="p-4 border-b border-white/10 flex items-center gap-3">
          <img src={ASSETS.brasao} alt="Brasão" className="h-8 w-8 bg-white rounded p-0.5 object-contain" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/70">Painel</div>
            <div className="text-sm font-black">MCMV Grajaú</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((i) => {
            const active = pathname === i.to || (i.to === "/admin" && pathname === "/admin/");
            return (
              <Link key={i.to} to={i.to}
                className={`flex items-center gap-3 px-3 py-2 rounded text-sm font-medium transition-colors ${active ? "bg-white text-[#083CA6]" : "text-white/90 hover:bg-white/10"}`}
                data-testid={`sidebar-${i.to.replace(/\//g, "-")}`}>
                <i.icon className="w-4 h-4" />{i.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10 text-xs">
          <div className="mb-2 text-white/80">{user?.nome}</div>
          <div className="text-white/60 mb-3">{user?.role === "super_admin" ? "Super Admin" : "Funcionário"}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => nav("/")}>
              <HomeIcon className="w-4 h-4 mr-1" />Site
            </Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={doLogout} data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-1" />Sair
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        {/* Top bar com sino de alertas (desktop) */}
        <div className="hidden md:flex items-center justify-end gap-3 bg-[#083CA6] text-white px-6 py-2 shadow-sm">
          <AlertsBell />
          <ChangePasswordDialog />
          <div className="text-xs">
            <div className="font-semibold">{user?.nome}</div>
            <div className="text-white/60 text-[10px]">
              {user?.role === "super_admin" ? "Super Admin" : "Funcionário"}
            </div>
          </div>
        </div>
        <div className="md:hidden bg-[#083CA6] text-white p-3 flex items-center justify-between gap-2">
          <div className="font-bold shrink-0">MCMV Grajaú</div>
          <div className="flex gap-2 overflow-x-auto">
            {items.map((i) => (
              <Link key={i.to} to={i.to} className="text-xs px-2 py-1 rounded bg-white/10 shrink-0">{i.label}</Link>
            ))}
            <button onClick={doLogout} className="text-xs px-2 py-1 rounded bg-white/10 shrink-0">Sair</button>
          </div>
        </div>
        <div className="p-6"><Outlet /></div>
      </main>
    </div>
  );
}
